import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCreditCard,
  faDollarSign,
  faChartLine,
  faTriangleExclamation,
  faCalendar,
  faCog,
  faCheckCircle,
  faClock,
  faBolt,
  faChartBar,
  faFileAlt,
  faTimes,
  faFileInvoice,
  faExternalLinkAlt,
  faExclamationTriangle,
  faTimesCircle,
  faComments,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { 
  getSubscriptionPlans, 
  getCurrentSubscription, 
  getPaymentMethods, 
  getInvoices,
  getApiBaseUrl
} from '../../services/api';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loader from '../../components/common/Loader';

interface SubscriptionPlan {
  id: string;
  name: string;
  display_name: string;
  tier: string;
  price_monthly_cents: number;
  price_yearly_cents?: number;
  max_messages_per_month: number;
  max_storage_gb: number;
  seo_pages_per_month: number;
  youtube_minutes_per_month: number;
  features: Record<string, any>;
  is_recommended: boolean;
}

interface Subscription {
  id: string;
  plan: {
    id: string;
    name: string;
    tier: string;
    price_monthly_cents: number;
    features: Record<string, any>;
  };
  status: string;
  billing_period: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  trial_end?: string;
  days_until_renewal: number;
}

interface UsageSummary {
  messages?: { used: number; overage: number; cost: number };
  storage?: { used: number; overage: number; cost: number };
  seo_pages?: { used: number; overage: number; cost: number };
  youtube_minutes?: { used: number; overage: number; cost: number };
  api_calls?: { used: number; overage: number; cost: number };
}

interface PaymentMethod {
  id: string;
  type: string;
  display_name: string;
  brand: string;
  last4: string;
  exp_month?: number;
  exp_year?: number;
  is_default: boolean;
}

interface Invoice {
  id: string;
  invoice_number: string;
  status: string;
  total_amount: number;
  currency: string;
  due_date?: string;
  paid_at?: string;
  period_start?: string;
  period_end?: string;
  pdf_url?: string;
  hosted_invoice_url?: string;
}

interface PaymentRequest {
  id: string;
  client_id: string;
  client_name: string;
  client_email: string;
  requested_by: string;
  requester_name: string;
  amount_cents: number;
  amount: number;
  currency: string;
  description: string;
  invoice_number?: string;
  status: string;
  payment_link_url?: string;
  is_payable: boolean;
  expires_at?: string;
  days_until_expiry?: number;
  paid_at?: string;
  email_sent: boolean;
  created_at: string;
  updated_at: string;
  metadata: any;
}

interface BillingData {
  subscription: Subscription | null;
  usage: UsageSummary;
  limits: {
    max_messages_per_month: number;
    max_storage_gb: number;
    seo_pages_per_month: number;
    youtube_minutes_per_month: number;
  };
}

const ClientBillingDashboard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [billingData, setBillingData] = useState<BillingData | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [paymentRequests, setPaymentRequests] = useState<PaymentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');

  // Initialize activeTab from URL parameter if present, otherwise default to 'overview'
  const getInitialTab = () => {
    const tab = searchParams.get('tab');
    const validTabs = ['overview', 'usage', 'plans', 'billing', 'payments'];
    return tab && validTabs.includes(tab) ? tab : 'overview';
  };
  const [activeTab, setActiveTab] = useState(getInitialTab());

  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [usageSummary, setUsageSummary] = useState<any>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);

  // Handler to change tab and update URL
  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    // Update URL to reflect current tab
    window.history.pushState({}, '', `/client/billing?tab=${newTab}`);
  };

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      try {
        setLoading(true);

        // Handle payment request status (non-subscription payments)
        const payment = searchParams.get('payment');
        const requestId = searchParams.get('request_id');

        if (payment === 'success' && requestId) {
          if (isMounted) {
            setPaymentSuccess(true);
          }
          toast.success('Payment completed successfully!');
          window.history.replaceState({}, '', '/client/billing?tab=payments');
          if (isMounted) {
            setActiveTab('payments');
          }
        } else if (payment === 'cancelled') {
          toast.info('Payment was cancelled');
          window.history.replaceState({}, '', '/client/billing?tab=payments');
          if (isMounted) {
            setActiveTab('payments');
          }
        }

        // Handle subscription checkout success/cancel
        const checkoutSuccess = searchParams.get('success');
        const checkoutCanceled = searchParams.get('canceled');

        if (checkoutSuccess === 'true') {
          toast.success('Subscription updated! Syncing your plan…');
          try {
            await axios.post(`${getApiBaseUrl()}/billing/sync-subscription`);
          } catch (error) {
            console.error('Error syncing subscription after checkout:', error);
            toast.error('Payment succeeded, but we could not refresh your plan automatically. Please refresh or contact support.');
          }
          window.history.replaceState({}, '', '/client/billing?tab=overview');
          if (isMounted) {
            setActiveTab('overview');
          }
        } else if (checkoutCanceled === 'true') {
          toast.info('Checkout was cancelled');
          window.history.replaceState({}, '', '/client/billing?tab=plans');
          if (isMounted) {
            setActiveTab('plans');
          }
        }

        await fetchBillingData();
        await fetchPlans();
        await fetchInvoices();
        await fetchPaymentRequests();
        await fetchUsageSummary();
        // Fetch payment methods last so it can clear the loading spinner
        await fetchPaymentMethods();
      } catch (error) {
        console.error('Error initializing billing dashboard:', error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update active tab based on subscription status (only when no URL tab is specified)
  useEffect(() => {
    const tab = searchParams.get('tab');
    // Only default to 'plans' if:
    // 1. User has no subscription
    // 2. No tab is specified in URL
    // 3. Current active tab is 'overview' (the default when no URL param)
    // 4. billingData has been loaded (not null)
    if (billingData && !billingData.subscription && !tab && activeTab === 'overview') {
      setActiveTab('plans');
    }
  }, [billingData, activeTab]);

  const fetchBillingData = async () => {
    try {
      const response = await getCurrentSubscription();
      
      setBillingData({
        subscription: response.subscription,
        usage: response.usage || {},
        limits: response.limits || {}
      });
    } catch (error) {
      console.error('Error fetching billing data:', error);
    }
  };

  const fetchPlans = async () => {
    try {
      const plans = await getSubscriptionPlans();
      setPlans(plans);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const data = await getPaymentMethods();
      setPaymentMethods(data.payment_methods || []);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      const data = await getInvoices(10);
      setInvoices(data.invoices || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const fetchPaymentRequests = async () => {
    try {
      const response = await axios.get(`${getApiBaseUrl()}/billing/payment-requests`);
      setPaymentRequests(response.data.payment_requests || []);
    } catch (error) {
      console.error('Error fetching payment requests:', error);
    }
  };

  const fetchUsageSummary = async () => {
    try {
      setLoadingUsage(true);
      const response = await axios.get(`${getApiBaseUrl()}/billing/usage/summary`);
      setUsageSummary(response.data);
    } catch (error) {
      console.error('Error fetching usage summary:', error);
    } finally {
      setLoadingUsage(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'trialing': return 'bg-blue-100 text-blue-800';
      case 'past_due': return 'bg-yellow-100 text-yellow-800';
      case 'canceled': return 'bg-red-100 text-red-800';
      case 'paused': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getUsagePercentage = (used: number, limit: number) => {
    return Math.min((used / limit) * 100, 100);
  };

  const formatCurrency = (cents: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(cents / 100);
  };

  const handleUpgradePlan = async (planId: string, billingPeriod: 'monthly' | 'yearly' = 'monthly') => {
    try {
      setLoading(true);
      // Create a checkout session for the selected plan
      const response = await axios.post(`${getApiBaseUrl()}/billing/checkout-session`, {
        plan_id: planId,
        billing_period: billingPeriod,
        success_url: window.location.origin + '/client/billing?success=true',
        cancel_url: window.location.origin + '/client/billing?canceled=true'
      });

      const { checkout_url, free_plan, success_url } = response.data;

      // Handle free plan (no Stripe checkout needed)
      if (free_plan) {
        // Redirect to success URL directly
        window.location.href = success_url;
        return;
      }

      // Redirect to Stripe Checkout for paid plans
      if (checkout_url) {
        window.location.href = checkout_url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to start checkout process. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    try {
      setLoading(true);
      // Create a setup session for adding payment method
      const response = await axios.post(`${getApiBaseUrl()}/billing/setup-payment-method`, {
        success_url: window.location.origin + '/client/billing?payment_method_added=true',
        cancel_url: window.location.origin + '/client/billing'
      });

      const { setup_url } = response.data;
      
      // Redirect to Stripe Setup
      window.location.href = setup_url;
    } catch (error) {
      console.error('Error setting up payment method:', error);
      alert('Failed to add payment method. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const UsageCard: React.FC<{
    title: string;
    used: number;
    limit: number;
    unit: string;
    icon: React.ReactNode;
    overage?: number;
    cost?: number;
  }> = ({ title, used, limit, unit, icon, overage = 0, cost = 0 }) => {
    const percentage = getUsagePercentage(used, limit);
    const isOverage = overage > 0;
    
    return (
      <Card className="relative">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          {icon}
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {used.toLocaleString()} <span className="text-sm font-normal text-gray-500">/ {limit.toLocaleString()} {unit}</span>
          </div>
          <Progress value={percentage} className="mt-2" />
          <div className="flex justify-between items-center mt-2 text-xs text-gray-600">
            <span>{percentage.toFixed(1)}% used</span>
            {isOverage && (
              <Badge variant="destructive" className="text-xs">
                +{overage.toLocaleString()} overage
              </Badge>
            )}
          </div>
          {cost > 0 && (
            <div className="mt-1 text-xs text-red-600">
              Overage cost: {formatCurrency(cost * 100)}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const PlanCard: React.FC<{ plan: SubscriptionPlan; isCurrentPlan: boolean; billingPeriod: 'monthly' | 'yearly' }> = ({
    plan,
    isCurrentPlan,
    billingPeriod
  }) => (
    <Card className={`relative h-full flex flex-col ${isCurrentPlan ? 'ring-2 ring-blue-500' : ''} ${plan.is_recommended ? 'border-blue-500' : ''}`}>
      {plan.is_recommended && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
          <div className="bg-black text-white px-4 py-1 rounded-full text-xs font-bold">
            RECOMMENDED
          </div>
        </div>
      )}
      {isCurrentPlan && (
        <div className="absolute -top-2 right-4">
          <Badge className="bg-green-500 text-white px-3 py-1">Current Plan</Badge>
        </div>
      )}
      
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-xl">{plan.display_name}</CardTitle>
            <CardDescription className="mt-2">{plan.tier}</CardDescription>
          </div>
          <div className="text-right">
            {billingPeriod === 'monthly' ? (
              <>
                <div className="text-3xl font-bold">
                  {formatCurrency(plan.price_monthly_cents)}
                </div>
                <div className="text-sm text-gray-500">per month</div>
              </>
            ) : (
              <>
                <div className="text-3xl font-bold">
                  {formatCurrency(plan.price_yearly_cents || plan.price_monthly_cents * 12)}
                </div>
                <div className="text-sm text-gray-500">per year</div>
                {plan.price_yearly_cents && (
                  <div className="text-xs text-green-600 mt-1">
                    {formatCurrency(plan.price_yearly_cents / 12)} per month
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="flex-grow flex flex-col">
        <div className="flex-grow">
          <div className="space-y-2 mb-4">
            <div className="flex items-center text-sm">
              <FontAwesomeIcon icon={faBolt} className="w-4 h-4 mr-2 text-gray-400" />
              {plan.max_messages_per_month.toLocaleString()} messages/month
            </div>
            <div className="flex items-center text-sm">
              <FontAwesomeIcon icon={faChartBar} className="w-4 h-4 mr-2 text-gray-400" />
              {plan.seo_pages_per_month === 9999999 ? 'Unlimited' : plan.seo_pages_per_month} SEO pages/month
            </div>
          </div>
          
          <div className="space-y-1 mb-4">
            {Object.entries(plan.features || {})
              .filter(([key]) => key !== 'api_access' && key !== 'widget_integration')
              .map(([key, value]) => {
                if (value && typeof value === 'object' && value.enabled) {
                  const featureNames: { [key: string]: string } = {
                    chat: 'Chat AI Assistant',
                    theming: 'Custom Theming',
                    analytics: 'Analytics Dashboard',
                    citations: 'Source Citations',
                    integrations: 'API Access',
                    seo_analysis: 'SEO Analysis',
                    qa_validation: 'QA Validation',
                    knowledge_base: 'Knowledge Base',
                    youtube_integration: 'YouTube Integration',
                    calendar_integration: 'Calendar Integration',
                    white_label: 'White Label',
                    team_management: 'Team Management',
                    support_flows: 'Support Flows',
                    dynamic_prompting: 'Dynamic Prompting',
                    source_preferences: 'Source Preferences',
                    voice_search: 'Voice Search',
                    virtual_tryon: 'Virtual Try-On',
                    priority_support: 'Priority Support',
                    dedicated_manager: 'Dedicated Account Manager'
                  };
                  
                  const displayName = featureNames[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                  
                  return (
                    <div key={key} className="flex items-center text-xs text-gray-600">
                      <FontAwesomeIcon icon={faCheckCircle} className="w-3 h-3 mr-2 text-green-500" />
                      {displayName}
                    </div>
                  );
                }
                return null;
              })}
          </div>
        </div>
        
        <div className="mt-auto">
          {!isCurrentPlan && (
            <Button
              className="w-full bg-black hover:bg-[#eab308] text-white transition-colors"
              onClick={() => handleUpgradePlan(plan.id, billingPeriod)}
            >
              Upgrade to {plan.display_name}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 8rem)' }}>
        <Loader 
          text="Loading billing information..." 
          variant="spinner"
          size="large"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Billing & Subscription</h1>
          <p className="text-gray-600 mt-1">Manage your subscription and billing</p>
        </div>
      </div>

      {/* Status Alert */}
      {billingData?.subscription?.status === 'past_due' && (
        <Alert className="border-red-200 bg-red-50">
          <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            Your subscription is past due. Please update your payment method to avoid service interruption.
          </AlertDescription>
        </Alert>
      )}

      {billingData?.subscription?.cancel_at_period_end && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <FontAwesomeIcon icon={faClock} className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            Your subscription will be canceled at the end of the current billing period on{' '}
            {formatDate(billingData.subscription.current_period_end)}.
          </AlertDescription>
        </Alert>
      )}

      {/* Always show tabs - allow users to browse and subscribe to plans */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className={`grid w-full ${
          !billingData?.subscription ? 'grid-cols-1' : 'grid-cols-5'
        }`}>
          {billingData?.subscription ? (
            <>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="usage">Usage</TabsTrigger>
              <TabsTrigger value="plans">Plans</TabsTrigger>
              <TabsTrigger value="billing">Billing</TabsTrigger>
              <TabsTrigger value="payments">Payments</TabsTrigger>
            </>
          ) : (
            <TabsTrigger value="plans">Available Plans</TabsTrigger>
          )}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Current Plan Card */}
          {billingData?.subscription && billingData.subscription.plan && (
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center">
                      {billingData.subscription.plan.name}
                      <Badge className={`ml-2 ${getStatusColor(billingData.subscription.status)}`}>
                        {billingData.subscription.status}
                      </Badge>
                    </CardTitle>
                    <CardDescription>
                      {billingData.subscription.billing_period} billing • Next renewal in {billingData.subscription.days_until_renewal} days
                    </CardDescription>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">
                      {formatCurrency(billingData.subscription.plan.price_monthly_cents)}
                    </div>
                    <div className="text-sm text-gray-500">per month</div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    <FontAwesomeIcon icon={faCalendar} className="w-4 h-4 inline mr-1" />
                    Current period: {formatDate(billingData.subscription.current_period_start)} - {formatDate(billingData.subscription.current_period_end)}
                  </div>
                  <div className="flex space-x-2">
                    {billingData.subscription.status === 'active' && !billingData.subscription.cancel_at_period_end && (
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={async () => {
                          if (window.confirm('Are you sure you want to cancel your subscription?')) {
                            try {
                              await axios.post(`${getApiBaseUrl()}/billing/subscription/${billingData.subscription!.id}/cancel`, {
                                at_period_end: true
                              });
                              window.location.reload();
                            } catch (error) {
                              console.error('Cancel failed:', error);
                              alert('Failed to cancel subscription. Please try again.');
                            }
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={faTimes} className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}


          {!billingData?.subscription && (
            <Card>
              <CardContent className="text-center py-8">
                <p className="text-gray-500 mb-4">You don't have an active subscription</p>
                <Button onClick={() => setActiveTab('plans')}>
                  View Available Plans
                </Button>
              </CardContent>
            </Card>
          )}

          {billingData?.subscription?.status === 'no_subscription' && (
            <Card>
              <CardContent className="text-center py-8">
                <p className="text-gray-500 mb-4">Start your subscription to access all features</p>
                <Button onClick={() => setActiveTab('plans')}>
                  Choose a Plan
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faDollarSign} className="w-8 h-8 text-green-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">This Month</p>
                    <p className="text-2xl font-bold">
                      {billingData?.subscription ?
                        formatCurrency(billingData.subscription.plan.price_monthly_cents) :
                        '$0.00'
                      }
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faChartLine} className="w-8 h-8 text-blue-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Messages Used</p>
                    <p className="text-2xl font-bold">
                      {usageSummary?.messages_used?.toLocaleString() || '0'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Invoices */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Invoices</CardTitle>
              <CardDescription>Your latest billing history</CardDescription>
            </CardHeader>
            <CardContent>
              {invoices.length > 0 ? (
                <div className="space-y-2">
                  {invoices.slice(0, 5).map((invoice) => (
                    <div
                      key={invoice.id}
                      className={`flex justify-between items-center p-3 border rounded-lg ${invoice.hosted_invoice_url ? 'hover:bg-gray-50 cursor-pointer transition-colors' : ''}`}
                      onClick={() => invoice.hosted_invoice_url && window.open(invoice.hosted_invoice_url, '_blank')}
                    >
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          {invoice.invoice_number}
                          {invoice.hosted_invoice_url && (
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {invoice.period_start && invoice.period_end &&
                            `${formatDate(invoice.period_start)} - ${formatDate(invoice.period_end)}`
                          }
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">{formatCurrency(invoice.total_amount * 100)}</div>
                        <Badge
                          className={invoice.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
                        >
                          {invoice.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No invoices yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Usage Tab */}
        <TabsContent value="usage" className="space-y-6">
          {usageSummary && usageSummary.has_subscription ? (
            <>
              {/* Usage Header Banner */}
              <Card className="border-l-4 border-l-blue-600">
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800 mb-1">
                        {usageSummary.plan_display_name || usageSummary.plan_name}
                      </h2>
                      <p className="text-gray-600">Current billing period usage</p>
                    </div>
                    <div className="mt-4 md:mt-0 flex items-center space-x-4">
                      <div className="text-right">
                        <p className="text-gray-600 text-sm">Period</p>
                        <p className="font-semibold">
                          {new Date(usageSummary.period_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(usageSummary.period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Overage Warning - Show when over limit */}
              {usageSummary.overage_messages > 0 && (
                <Alert className="border-red-300 bg-red-50">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600" />
                  <AlertTitle>Over Limit - Overage Charges Apply</AlertTitle>
                  <AlertDescription>
                    <p className="mb-2">
                      You've used <strong>{usageSummary.overage_messages.toLocaleString()} extra messages</strong> beyond your plan limit.
                      These will be charged at your overage rate.
                    </p>
                    {usageSummary.overage_cost_cents > 0 && (
                      <div className="bg-red-100 border border-red-200 rounded p-3 mt-2">
                        <div className="flex items-center justify-between">
                          <span className="text-red-900 font-medium">Estimated Overage Cost:</span>
                          <span className="text-red-900 text-lg font-bold">
                            {formatCurrency(usageSummary.overage_cost_cents)}
                          </span>
                        </div>
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {/* Usage Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Messages Used Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FontAwesomeIcon icon={faComments} className="text-blue-600" />
                      </div>
                      <div>
                        <CardDescription>Messages Used</CardDescription>
                        <CardTitle className="text-2xl">
                          {usageSummary.messages_used.toLocaleString()}
                        </CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Limit: {usageSummary.messages_limit.toLocaleString()}</span>
                        <span className="font-semibold">{((usageSummary.messages_used / usageSummary.messages_limit) * 100).toFixed(1)}%</span>
                      </div>
                      <Progress
                        value={Math.min((usageSummary.messages_used / usageSummary.messages_limit) * 100, 100)}
                        className="h-2"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Status Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        usageSummary.overage_messages > 0 ? 'bg-red-100' :
                        (usageSummary.messages_used / usageSummary.messages_limit) >= 0.9 ? 'bg-yellow-100' :
                        'bg-green-100'
                      }`}>
                        <FontAwesomeIcon
                          icon={usageSummary.overage_messages > 0 ? faExclamationTriangle : faCheckCircle}
                          className={
                            usageSummary.overage_messages > 0 ? 'text-red-600' :
                            (usageSummary.messages_used / usageSummary.messages_limit) >= 0.9 ? 'text-yellow-600' :
                            'text-green-600'
                          }
                        />
                      </div>
                      <div>
                        <CardDescription>Status</CardDescription>
                        <CardTitle className={`text-2xl ${
                          usageSummary.overage_messages > 0 ? 'text-red-600' :
                          (usageSummary.messages_used / usageSummary.messages_limit) >= 0.9 ? 'text-yellow-600' :
                          'text-green-600'
                        }`}>
                          {usageSummary.overage_messages > 0 ? 'Over Limit' : 'Healthy'}
                        </CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{usageSummary.overage_messages > 0 ? 'Over Limit' : 'Available'}</span>
                        <span className={`font-semibold ${usageSummary.overage_messages > 0 ? 'text-red-600' : 'text-gray-800'}`}>
                          {usageSummary.overage_messages > 0
                            ? `+${usageSummary.overage_messages.toLocaleString()}`
                            : Math.max(0, usageSummary.messages_limit - usageSummary.messages_used).toLocaleString()
                          }
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Limit</span>
                        <span className="font-semibold text-gray-800">{usageSummary.messages_limit.toLocaleString()}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Cost Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <FontAwesomeIcon icon={faDollarSign} className="text-green-600" />
                      </div>
                      <div>
                        <CardDescription>Total Cost</CardDescription>
                        <CardTitle className="text-2xl">
                          {formatCurrency(usageSummary.total_cost_cents || 0)}
                        </CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Base Cost</span>
                        <span className="font-semibold text-gray-800">
                          {formatCurrency((usageSummary.total_cost_cents || 0) - (usageSummary.overage_cost_cents || 0))}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Overage</span>
                        <span className={`font-semibold ${(usageSummary.overage_cost_cents || 0) > 0 ? 'text-yellow-600' : 'text-gray-600'}`}>
                          {formatCurrency(usageSummary.overage_cost_cents || 0)}
                        </span>
                      </div>
                      <div className="pt-2 border-t flex justify-between">
                        <span className="text-gray-800 font-medium">Total</span>
                        <span className="font-bold text-gray-800">
                          {formatCurrency(usageSummary.total_cost_cents || 0)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Info Banner */}
              <Alert>
                <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600" />
                <AlertTitle>Usage Information</AlertTitle>
                <AlertDescription>
                  <p className="text-sm">
                    <strong>Message Usage:</strong> Chat messages reset monthly on your billing date.
                    {usageSummary.plan_tier === 'free' ? (
                      <span className="ml-1">• <strong>No overage allowed</strong> on Free plan - upgrade to continue.</span>
                    ) : (
                      <span className="ml-1">• Extra messages are charged at overage rates and added to your next invoice.</span>
                    )}
                  </p>
                </AlertDescription>
              </Alert>
            </>
          ) : (
            <Card>
              <CardContent className="text-center py-8">
                <FontAwesomeIcon icon={faChartLine} className="text-gray-300 text-5xl mb-4" />
                <p className="text-gray-500 mb-4">No usage data available</p>
                <p className="text-sm text-gray-400">Subscribe to a plan to start tracking your usage</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Plans Tab */}
        <TabsContent value="plans" className="space-y-6">
          {!billingData?.subscription && (
            <Alert className="border-blue-200 bg-blue-50">
              <FontAwesomeIcon icon={faBolt} className="text-blue-600" />
              <AlertDescription className="text-blue-800">
                <strong>Choose Your Plan</strong> - Select a subscription plan below to get started. Click "Upgrade to [Plan Name]" to begin the checkout process.
              </AlertDescription>
            </Alert>
          )}

          {/* Billing Period Toggle */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-6 py-2 rounded-md font-medium transition-colors ${
                  billingPeriod === 'monthly'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`px-6 py-2 rounded-md font-medium transition-colors ${
                  billingPeriod === 'yearly'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Yearly
                <span className="ml-2 text-xs text-green-600 font-semibold">Save 17%</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
            {plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                isCurrentPlan={billingData?.subscription?.plan.id === plan.id}
                billingPeriod={billingPeriod}
              />
            ))}
          </div>
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing" className="space-y-6">
          {/* Payment Methods */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Payment Methods</CardTitle>
                  <CardDescription>Manage your payment methods</CardDescription>
                </div>
                <Button onClick={handleAddPaymentMethod}>
                  <FontAwesomeIcon icon={faCreditCard} className="w-4 h-4 mr-2" />
                  Add Payment Method
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {paymentMethods.length > 0 ? (
                <div className="space-y-3">
                  {paymentMethods.map((method) => (
                    <div key={method.id} className="flex justify-between items-center p-4 border rounded-lg">
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faCreditCard} className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <div className="font-medium">{method.display_name}</div>
                          {method.exp_month && method.exp_year && (
                            <div className="text-sm text-gray-500">
                              Expires {method.exp_month}/{method.exp_year}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {method.is_default && (
                          <Badge className="bg-blue-100 text-blue-800">Default</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No payment methods added</p>
              )}
            </CardContent>
          </Card>

          {/* Invoices */}
          <Card>
            <CardHeader>
              <CardTitle>Invoices</CardTitle>
              <CardDescription>Your billing history</CardDescription>
            </CardHeader>
            <CardContent>
              {invoices.length > 0 ? (
                <div className="space-y-2">
                  {invoices.map((invoice) => (
                    <div key={invoice.id} className="flex justify-between items-center p-4 border rounded-lg">
                      <div>
                        <div className="font-medium">{invoice.invoice_number}</div>
                        <div className="text-sm text-gray-500">
                          {invoice.period_start && invoice.period_end &&
                            `${formatDate(invoice.period_start)} - ${formatDate(invoice.period_end)}`
                          }
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <div className="font-medium">{formatCurrency(invoice.total_amount * 100)}</div>
                          <Badge
                            className={invoice.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
                          >
                            {invoice.status}
                          </Badge>
                        </div>
                        <div className="flex space-x-2">
                          {invoice.hosted_invoice_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(invoice.hosted_invoice_url, '_blank')}
                            >
                              <FontAwesomeIcon icon={faFileAlt} className="w-4 h-4 mr-2" />
                              View Invoice
                            </Button>
                          )}
                          {invoice.pdf_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(invoice.pdf_url, '_blank')}
                            >
                              <FontAwesomeIcon icon={faFileAlt} className="w-4 h-4 mr-2" />
                              PDF
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No invoices yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Requests Tab */}
        <TabsContent value="payments" className="space-y-6">
          {/* Success Alert */}
          {paymentSuccess && (
            <Alert className="border-green-200 bg-green-50">
              <FontAwesomeIcon icon={faCheckCircle} className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Payment completed successfully! Thank you for your payment.
              </AlertDescription>
            </Alert>
          )}
          
          {/* Pending Payment Requests */}
          {paymentRequests.filter(r => r.status === 'pending').length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FontAwesomeIcon icon={faClock} className="w-5 h-5 text-yellow-500 mr-2" />
                  Pending Payment Requests
                </CardTitle>
                <CardDescription>
                  One-time payments requested by your service provider
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {paymentRequests
                    .filter(r => r.status === 'pending')
                    .map((request) => (
                      <div key={request.id} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="text-lg font-semibold">
                                {formatCurrency(request.amount_cents, request.currency)}
                              </h4>
                              {request.days_until_expiry !== undefined && request.days_until_expiry !== null && request.days_until_expiry <= 3 && (
                                <Badge className="bg-orange-100 text-orange-800">
                                  <FontAwesomeIcon icon={faExclamationTriangle} className="w-3 h-3 mr-1" />
                                  Expires in {request.days_until_expiry} days
                                </Badge>
                              )}
                            </div>
                            <p className="text-gray-700 mb-2">{request.description}</p>
                            <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                              {request.invoice_number && (
                                <div className="flex items-center gap-1">
                                  <FontAwesomeIcon icon={faFileInvoice} className="w-3 h-3" />
                                  Invoice: {request.invoice_number}
                                </div>
                              )}
                              <div className="flex items-center gap-1">
                                <FontAwesomeIcon icon={faCalendar} className="w-3 h-3" />
                                Created: {formatDate(request.created_at)}
                              </div>
                            </div>
                          </div>
                          {request.is_payable && request.payment_link_url && (
                            <Button
                              onClick={() => window.open(request.payment_link_url, '_blank')}
                              className="ml-4"
                            >
                              <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-2" />
                              Pay Now
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  }
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payment History */}
          <Card>
            <CardHeader>
              <CardTitle>Payment History</CardTitle>
              <CardDescription>Your completed one-time payments</CardDescription>
            </CardHeader>
            <CardContent>
              {paymentRequests.filter(r => r.status === 'paid').length > 0 ? (
                <div className="space-y-2">
                  {paymentRequests
                    .filter(r => r.status === 'paid')
                    .map((request) => (
                      <div key={request.id} className="flex justify-between items-center p-4 border rounded-lg">
                        <div>
                          <div className="font-medium">
                            {formatCurrency(request.amount_cents, request.currency)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {request.description}
                            {request.invoice_number && ` • Invoice: ${request.invoice_number}`}
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className="bg-green-100 text-green-800">
                            <FontAwesomeIcon icon={faCheckCircle} className="w-3 h-3 mr-1" />
                            Paid
                          </Badge>
                          {request.paid_at && (
                            <div className="text-sm text-gray-500 mt-1">
                              {formatDate(request.paid_at)}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  }
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No payment history yet</p>
              )}
            </CardContent>
          </Card>

          {/* No Payment Requests */}
          {paymentRequests.length === 0 && (
            <Card>
              <CardContent className="text-center py-12">
                <FontAwesomeIcon icon={faCheckCircle} className="text-4xl text-green-500 mb-4" />
                <p className="text-gray-500">No payment requests at this time</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClientBillingDashboard;
